/**
 * GTM (Google Tag Manager) service.
 * Pushes business events to window.dataLayer for Google Ads, GA4, and other GTM tags.
 *
 * Uses Google's standard event names for maximum compatibility:
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 *
 * All methods are no-ops when VITE_APP_GTM_ENABLED !== 'true'.
 */

const ENABLED = import.meta.env.VITE_APP_GTM_ENABLED === 'true'

function push(event: string, data: Record<string, unknown> = {}): void {
  if (!ENABLED) return
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push({ event, ...data })
}

export const gtmService = {
  // -----------------------------------------------------------------------
  // Auth events
  // -----------------------------------------------------------------------

  /** User logged in — Google standard event */
  trackLogin(method: 'email' | 'google' | 'oauth' = 'email'): void {
    push('login', { method })
  },

  /** New user registered — Google standard event */
  trackSignUp(method: 'email' | 'google' | 'oauth' = 'email'): void {
    push('sign_up', { method })
  },

  // -----------------------------------------------------------------------
  // Reservation funnel — Google standard e-commerce events
  // -----------------------------------------------------------------------

  /** User starts looking at a lake / reservation funnel entry point */
  trackViewItem(data: { lakeId: string; lakeName?: string; orgSlug?: string }): void {
    push('view_item', {
      items: [{ item_id: data.lakeId, item_name: data.lakeName ?? data.lakeId, item_category: 'lake' }],
      org_slug: data.orgSlug,
    })
  },

  /** User starts the reservation form — Google: begin_checkout */
  trackBeginCheckout(data: { lakeId: string; spotId?: string; spotName?: string }): void {
    push('begin_checkout', {
      items: [{ item_id: data.lakeId, item_name: data.spotName ?? data.spotId ?? data.lakeId, item_category: 'fishing_spot' }],
    })
  },

  /** Reservation successfully completed — Google: purchase (main Google Ads conversion) */
  trackPurchase(data: { reservationId: string; orgSlug: string; value?: number; currency?: string }): void {
    push('purchase', {
      transaction_id: data.reservationId,
      org_slug: data.orgSlug,
      value: data.value ?? 0,
      currency: data.currency ?? 'EUR',
      items: [{ item_id: data.reservationId, item_category: 'reservation' }],
    })
  },

  // -----------------------------------------------------------------------
  // Onboarding / manager events
  // -----------------------------------------------------------------------

  /** Manager connected Stripe account */
  trackStripeConnected(data: { orgSlug?: string; chargesEnabled?: boolean }): void {
    push('stripe_account_connected', {
      org_slug: data.orgSlug,
      charges_enabled: data.chargesEnabled ?? false,
    })
  },

  /** First client reservation received (manager side) */
  trackFirstClientReservation(data: { organizationId: string; reservationId?: string }): void {
    push('first_client_reservation', {
      organization_id: data.organizationId,
      reservation_id: data.reservationId,
    })
  },

  /** Manager completed the onboarding guide */
  trackOnboardingCompleted(guideId: string): void {
    push('onboarding_completed', { guide_id: guideId })
  },

  // -----------------------------------------------------------------------
  // Engagement / lead events
  // -----------------------------------------------------------------------

  /** User clicked a support/help button — useful for measuring support funnel */
  trackSupportClicked(supportType: string): void {
    push('support_clicked', { support_type: supportType })
  },

  /** Page view (called from router — supplements vue-gtm's automatic tracking with custom data) */
  trackPageView(path: string, title?: string): void {
    push('page_view', { page_path: path, page_title: title ?? document.title })
  },
}

export default gtmService
